"use client";

import { useEffect, useState } from "react";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useStrikes } from "#/hooks/useStrikes";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { nextMaturities } from "#/utils/maturities";

import {
  findClosestATMStrikeIndex,
  showComboWarningToast,
} from "../StrategySelector.utils";

export const useStrangle = () => {
  const {
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLegs,
    buildInstrument,
  } = useComboInstrumentsController();
  const { marketIndexPrice } = useMarketIndexPrice();

  const [maturityIndex, setMaturityIndex] = useState(0);

  const maturities = nextMaturities();
  const maturity = maturities[maturityIndex];
  const { strikes } = useStrikes(maturity);

  // Apply long strangle: buy 1 OTM put (below ATM), buy 1 OTM call (above ATM)
  // Note: Users can flip directions to create short strangle (sell both legs)
  const applyStrangle = () => {
    const atmIndex = findClosestATMStrikeIndex(strikes, marketIndexPrice);
    const lowerIndex = atmIndex > 0 ? atmIndex - 1 : 0;
    const upperIndex =
      atmIndex < strikes.length - 1 ? atmIndex + 1 : strikes.length - 1;

    if (lowerIndex === upperIndex) {
      if (maturityIndex + 1 < maturities.length) {
        setMaturityIndex(maturityIndex + 1);
        return;
      }

      showComboWarningToast();
      return;
    }

    const lowerStrike = strikes[lowerIndex];
    const upperStrike = strikes[upperIndex];

    if (lowerStrike === undefined || upperStrike === undefined) {
      showComboWarningToast();
      return;
    }

    handleAddLegs([
      buildInstrument({
        type: "options",
        side: "buy",
        optionType: "put",
        strike: lowerStrike,
        size: "1",
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: "buy",
        optionType: "call",
        strike: upperStrike,
        size: "1",
        maturity,
      }),
    ]);
  };

  useEffect(() => {
    if (maturityIndex > 0) {
      // Retry strangle on maturity change to test all maturities
      applyStrangle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only rerun when maturityIndex changes
  }, [maturityIndex]);

  const isActivePatternStrangle = () => {
    if (comboPerpInstruments.length > 0 || comboOptionInstruments.length !== 2)
      return false;

    const [a, b] = comboOptionInstruments.toSorted(
      (x, y) => x.strike - y.strike,
    );

    if (
      a.maturity !== b.maturity ||
      a.underlying !== b.underlying ||
      a.side !== b.side || // Both legs must have same side (both buy OR both sell)
      a.optionType !== "put" ||
      b.optionType !== "call" ||
      a.strike === b.strike ||
      a.size !== b.size
    ) {
      return false;
    }

    return true;
  };

  return { applyStrangle, isActivePatternStrangle };
};
